import {CSSProperties} from 'react';

/**
 * Minimum measured height (px) for which the viewport cap is applied.
 *
 * The cap exists to keep a FULL-PAGE grid inside its scroll viewport. A grid
 * whose top edge is so close to the scrollport bottom that less than this
 * remains can never be that viewport-filling element — the remainder would not
 * even fit the grid's own chrome plus a couple of rows (toolbar ~44 +
 * active-filter bar ~40 + column header ~30 + pagination ~48 ≈ 162, plus
 * 3 DEFAULT rows × 36 = 108 → ~270). Such a measurement instead means the grid
 * is EMBEDDED mid-way down a scrollable form (e.g. the material detail dialog,
 * T20-137193) where the bounded parent (`<Box height={…}>` + `h-full`) is the
 * intended constraint — applying the smaller cap there squashed the rows area
 * to a sliver. Below this threshold the measurement is treated like the
 * below-the-fold case: no cap, the parent drives the height.
 */
const MIN_HEIGHT_CAP_PX = 270;

/**
 * Compute the inline `style` object for the EagGrid wrapper div.
 *
 * ### Height
 *
 * | Condition | Result |
 * |---|---|
 * | `autoHeight` is true | No height — grid expands with content |
 * | `availableHeightPx === 0` | No height — grid is below the fold or not yet measured; `h-full` fills the parent |
 * | `availableHeightPx < MIN_HEIGHT_CAP_PX` | No height — too small to be a real full-page-fit constraint; the grid is embedded mid-scrollport and the bounded parent drives the height (T20-137193) |
 * | otherwise | `{maxHeight: availableHeightPx}` — `h-full` fills the parent, cap stays inside the viewport |
 *
 * Height is intentionally never applied as a hard inline `height`: a one-shot
 * parent measurement caused two opposite failure modes — too short when the
 * parent grew later (stale measurement), and too tall when the parent's
 * `offsetHeight` was itself driven by the grid (circular flex sizing would lift
 * the cap and the grid would grow past the viewport).  `h-full + maxHeight`
 * lets the parent constrain the grid via flex while the viewport-bottom cap
 * stays in effect regardless.
 *
 * ### Width
 *
 * | Condition | Result |
 * |---|---|
 * | `availableWidthPx > 0` | `{maxWidth: availableWidthPx}` — constrain to measured parent width |
 * | otherwise | No width — let CSS `max-w-full` (100%) handle it |
 *
 * ### Why width matters
 *
 * When EagGrid is placed inside a flex-row layout (e.g. `Sections` with a sidebar),
 * the CSS `width: 100%` on ancestor boxes can resolve to the entire parent width rather than
 * the remaining space. The `availableWidthPx` measurement (from `useAvailableSizePx`) computes
 * the true available horizontal space and applies it as `maxWidth`, preventing overflow.
 *
 * @param autoHeight - When true the grid sizes itself to its content; no height
 *   cap is ever applied.
 * @param availableHeightPx - Pixels from the wrapper's top to the viewport
 *   bottom (from `useAvailableSizePx`).  `0` means unmeasured / below fold.
 * @param availableWidthPx - Pixels from the wrapper's left edge to the parent's
 *   right edge (from `useAvailableSizePx`). `0` means unmeasured. Defaults to `0`.
 */
export function getContainerStyle(
  autoHeight: boolean,
  availableHeightPx: number,
  availableWidthPx: number = 0
): CSSProperties | undefined {
  const heightStyle: CSSProperties | undefined =
    !autoHeight && availableHeightPx >= MIN_HEIGHT_CAP_PX
      ? {maxHeight: availableHeightPx}
      : undefined;

  const widthStyle: CSSProperties | undefined =
    availableWidthPx > 0 ? {maxWidth: availableWidthPx} : undefined;

  if (!heightStyle && !widthStyle) {
    return undefined;
  }
  return {...heightStyle, ...widthStyle};
}

/**
 * Compute the `className` string for the EagGrid wrapper div.
 *
 * Non-autoHeight grids always get `h-full` (Tailwind `height: 100%`) so they
 * fill their bounded parent — matching the old datagrid's `height: 100%`
 * behaviour.  When the viewport edge is the binding constraint, an inline
 * `style.height` from {@link getContainerStyle} overrides `h-full` via
 * CSS specificity.  autoHeight grids intentionally have no height constraint.
 *
 * ### Stacking context isolation
 *
 * Both variants include `isolate` (CSS `isolation: isolate`) to create a new
 * stacking context on the grid wrapper.  Without it, internal z-indices
 * (e.g. sticky action cells at z-9, action header at z-120, the grid's own
 * sidebar panel at z-301) leak into the page's stacking context and can
 * overlap external elements like the DMS activity sidebar (z-100).
 *
 * ### Inline-size containment (`[contain:inline-size]`)
 *
 * The grid table uses `table-fixed` with explicit per-column pixel widths, so
 * its intrinsic (min-content) width is the sum of all column widths — easily
 * thousands of pixels.  `overflow-auto` on the scroll body clips the table
 * visually but does NOT stop that intrinsic width from propagating upward:
 * any ancestor flex item with the default `min-width: auto` then refuses to
 * shrink below it, blowing the surrounding layout apart (e.g. the receive
 * note "Add material" modal, where the basket panel was pushed off-screen).
 * The `useAvailableSizePx` `maxWidth` cannot help there — it measures the
 * parent, whose size is itself driven by the grid, so the measurement locks
 * onto the overflowed width (a self-referential fixpoint).
 *
 * `contain: inline-size` makes the wrapper's intrinsic inline size independent
 * of its contents, so the grid is always a width-follower and never a
 * width-driver: ancestors size themselves purely from the available space,
 * CSS shrinks the grid to fit, and the JS measurement then converges on the
 * true available width.  Height is unaffected (autoHeight keeps working).
 *
 * @param wrapperStyle - Visual style variant from grid definition: `'INLINE'`
 *   omits the card border/shadow; `'CARD'` (default) includes them.
 * @param autoHeight - When true no `h-full` is added.
 * @param isMobile - When true always uses the inline (borderless) variant.
 */
export function getWrapperClassName(
  wrapperStyle: 'INLINE' | 'CARD',
  autoHeight: boolean,
  isMobile: boolean
): string {
  const heightFallback = autoHeight ? '' : ' h-full';
  const inlineClass = `w-full max-w-full min-w-0 [contain:inline-size] flex flex-col overflow-hidden isolate bg-white${heightFallback}`;
  const cardClass = `w-full max-w-full min-w-0 [contain:inline-size] flex flex-col overflow-hidden isolate bg-white border border-slate-200 shadow-sm rounded${heightFallback}`;

  if (isMobile) {
    return inlineClass;
  }
  return wrapperStyle === 'INLINE' ? inlineClass : cardClass;
}
